import Image from 'next/image';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Content from '@/components/Content';
import { ContentProps } from '@/models/fragments';
import CallToAction from '@/components/CallToAction';
import ContactCTA from '@/components/ContactCTA';

interface Asset {
	sys: {
		id: string;
	};
	url: string;
	description: string;
}

interface AssetLink {
	block: Asset[];
}

export interface Content {
	json: any;
	links: {
		assets: AssetLink;
		entries?: {
			block: {
				__typename: string;
				sys: {
					id: string;
				};
			}[];
		};
	};
}

function RichTextAsset({ id, assets }: { id: string; assets: Asset[] | undefined }) {
	const asset = assets?.find((asset) => asset.sys.id === id);

	if (asset?.url) {
		return (
			<Image
				className="!relative"
				src={asset.url}
				alt={asset.description}
				fill
				sizes="100vw"
			/>
		);
	}

	// Return null if the asset is not found
	return null;
}

export function Markdown({ content, className }: { content: Content; className?: string }) {
	if (!content || !content.json) {
		return null;
	}

	const getEntryById = (id: string) => {
		const entry = content?.links?.entries?.block?.find?.((link) => link?.sys?.id === id);
		return entry?.['__typename'];
	};

	const getFullEntryById = (id?: string) => {
		const entry = content?.links?.entries?.block?.find?.((link) => link?.sys?.id === id);
		return entry;
	};

	return (
		<div
			className={`[&_table]:table-auto [&_table]:w-full [&_table]:border-collapse [&_table]:rounded-lg [&_table]:shadow-sm [&_p]:text-lg  markdown   [&_b]:font-extrabold [&_th]:bg-primary-600 [&_th]:text-white [&_th]:font-bold [&_th]:text-left [&_th]:p-3 [&_th]:md:p-4 [&_th]:border-b-2 [&_th]:border-primary-700 [&_th]:text-sm [&_th]:md:text-base [&_th]:whitespace-nowrap [&_td]:p-3 [&_td]:md:p-4 [&_td]:border-b [&_td]:border-gray-200 [&_td]:align-top [&_td]:text-sm [&_td]:md:text-base [&_tr]:hover:bg-gray-50 [&_tr:nth-child(even)]:bg-gray-50/50 [&_tbody_tr:last-child_td]:border-b-0 hover:[&_a]:underline [&_img]:!max-h-[450px] [&_img]:object-cover [&_img]:h-auto [&_td_img]:w-full [&_td_img]:block [&_td_img]:!aspect-auto [&_td_img]:max-h-[200px] [&_td_img]:object-contain font-body gap-8 [&_p]:leading-wide flex flex-col [&_a]:!font-extrabold [&>table]:my-6 ${className}`}
		>
			<div className="[&_table]:overflow-x-auto [&_table]:block [&_table]:md:table [&_table]:-mr-4 [&_table]:md:mr-0">
				{documentToReactComponents(content.json, {
					renderNode: {
						[BLOCKS.TABLE]: (node: any, children: any) => (
							<div
								className="overflow-x-auto my-6 md:mx-0 touch-pan-x"
								style={{
									marginLeft: '-1rem',
									marginRight: '-1rem',
									paddingLeft: '1rem',
									paddingRight: '1rem',
									WebkitOverflowScrolling: 'touch',
									scrollBehavior: 'smooth',
								}}
							>
								<div className="inline-block min-w-full align-middle">
									<div className="overflow-hidden md:rounded-lg">
										<table className="min-w-full divide-y divide-gray-200">
											{children}
										</table>
									</div>
								</div>
							</div>
						),
						[BLOCKS.EMBEDDED_ASSET]: (node: any) => (
							<RichTextAsset
								id={node.data.target.sys.id}
								assets={content.links?.assets?.block}
							/>
						),
						[BLOCKS.PARAGRAPH]: (node: any, children: any) => (
							<p className="text-base md:text-lg mb-4">{children}</p>
						),
						[BLOCKS.HEADING_1]: (node: any, children: any) => (
							<h1 className="mt-12">{children}</h1>
						),
						[BLOCKS.HEADING_2]: (node: any, children: any) => (
							<h2 className="mt-10">{children}</h2>
						),
						[BLOCKS.HEADING_3]: (node: any, children: any) => (
							<h3 className="mt-18">{children}</h3>
						),
						[BLOCKS.HEADING_4]: (node: any, children: any) => (
							<h4 className="mt-8">{children}</h4>
						),
						[BLOCKS.HEADING_5]: (node: any, children: any) => (
							<h5 className="mt-8">{children}</h5>
						),
						[BLOCKS.HEADING_6]: (node: any, children: any) => (
							<h6 className="mt-6">{children}</h6>
						),
						[BLOCKS.UL_LIST]: (node: any, children: any) => (
							<div className=" ">
								<ul className=" px-0">{children}</ul>
							</div>
						),
						[BLOCKS.OL_LIST]: (node: any, children: any) => (
							<div className=" ">
								<ol className=" px-0">{children}</ol>
							</div>
						),
						[BLOCKS.LIST_ITEM]: (node: any, children: any) => (
							<li className=" px-0">{children}</li>
						),
						[BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
							const entryType = getEntryById(node.data.target.sys.id);

							switch (entryType) {
								case 'MarkdownCallToAction':
									const ctaData = getFullEntryById(node.data.target.sys.id) as
										| { content: ContentProps }
										| undefined;

									return (
										<div className="mt-10 md:mt-14">
											<ContactCTA
												title={ctaData?.content?.title}
												description={
													ctaData?.content?.contentDescription && (
														<Markdown
															content={ctaData.content.contentDescription}
														/>
													)
												}
												buttons={ctaData?.content?.buttonsCollection?.items}
											/>
										</div>
									);
								case 'Alert':
									const alertData = getFullEntryById(node.data.target.sys.id) as
										| {
												title: string;
												alertDescription: Content;
										  }
										| undefined;

									return (
										<div className=" ">
											<div className="bg-primary-50 p-4 rounded-md flex gap-2">
												<FontAwesomeIcon
													icon={['far', 'info-circle']}
													size="xl"
												/>
												<div>
													<p className="font-semibold text-lg">
														{alertData?.title}
													</p>
													{alertData?.alertDescription && (
														<Markdown
															content={alertData?.alertDescription}
														/>
													)}
												</div>
											</div>
										</div>
									);
								case 'Content':
									const contentData = getFullEntryById(node.data.target.sys.id) as
										| ContentProps
										| undefined;
									return (
										<Content
											className="px-0"
											description={contentData?.contentDescription}
											title={contentData?.title}
											caption={contentData?.caption}
											image={contentData?.image!}
											buttons={contentData?.buttonsCollection?.items}
											iconList={contentData?.iconListCollection?.items}
											imageCover={'cover'}
											reverse={contentData?.reverse}
										/>
									);

								case 'CustomImage':
									const customImageData = getFullEntryById(
										node.data.target.sys.id
									) as
										| {
												customImage: {
													url: string;
													title: string;
												};
												customImageContain: boolean;
												customImageHeight: number;
										  }
										| undefined;

									return (
										customImageData?.customImage?.url && (
											<Image
												className=" max-h-none custom-image"
												style={{
													height:
														customImageData?.customImageHeight ??
														'auto',
													width: '100%',
													objectFit: customImageData?.customImageContain
														? 'contain'
														: 'cover',
												}}
												src={customImageData?.customImage?.url}
												alt={customImageData?.customImage?.title}
												width={2000}
												height={2000}
											/>
										)
									);

								default:
									return null;
							}
						},
					},
				})}
			</div>
		</div>
	);
}
